import { useEffect, useRef, useState } from "react";
import type {
  WorkSettingsData,
  WorkSettingsDefinition,
  WorkSettingsSelection,
  WorkSettingsSnapshot,
} from "../../../../shared/work-settings";
import { readSettings, type SettingsScope } from "./api";
export function useWorkSettings(
  scope: SettingsScope | null,
  scopeKey: string,
  open: boolean,
  /** The workflow whose form and snapshot to read; null reads nothing. */
  workflow: string | null,
) {
  const [workflowId, setWorkflowId] = useState("");
  const [formData, setFormData] = useState<WorkSettingsData>({});
  const [applied, setApplied] = useState<WorkSettingsSelection | null>(null);
  const [definitions, setDefinitions] = useState<WorkSettingsDefinition[]>([]);
  const [snapshot, setSnapshot] = useState<WorkSettingsSnapshot | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const latest = useRef(scope);
  latest.current = scope;
  useEffect(() => {
    setWorkflowId("");
    setFormData({});
    setApplied(null);
    setDefinitions([]);
    setSnapshot(null);
    setError("");
  }, [scopeKey]);
  const token = scope?.token;
  useEffect(() => {
    if (!open || !latest.current) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void readSettings(latest.current, workflow, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setDefinitions(result.definitions);
          setSnapshot(result.snapshot);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setError(String(error.message ?? error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [scopeKey, open, refresh, token, workflow]);
  return {
    workflowId,
    formData,
    applied,
    definitions,
    snapshot,
    error,
    loading,
    setFormData,
    setApplied,
    reload: () => setRefresh((value) => value + 1),
    selectWorkflow: (id: string) => {
      setWorkflowId(id);
      setFormData({});
      setApplied(null);
    },
    clear: () => {
      setWorkflowId("");
      setFormData({});
      setApplied(null);
    },
  };
}
